import React, { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { ChevronDown, ArrowUpRight, FileText, User, Download, Mail } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { PROFILE, CITATIONS } from '../mock/data';

const COURTHOUSE_BG = 'https://images.unsplash.com/photo-1711003596872-aa68f08a4b8e?auto=format&fit=crop&w=1920&q=60';

const Hero = ({ onQuickAccess }) => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const heroRef = useRef(null);
  const [mouse, setMouse] = useState({ x: 0.5, y: 0.5 });

  useEffect(() => {
    const el = heroRef.current;
    if (!el) return;
    const onMove = (e) => {
      const rect = el.getBoundingClientRect();
      const x = (e.clientX - rect.left) / rect.width;
      const y = (e.clientY - rect.top) / rect.height;
      setMouse({ x, y });
      el.style.setProperty('--mx', `${x * 100}%`);
      el.style.setProperty('--my', `${y * 100}%`);
    };
    el.addEventListener('mousemove', onMove);
    return () => el.removeEventListener('mousemove', onMove);
  }, []);

  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  // positions for floating citations
  const citationPositions = [
    { top: '12%', left: '6%' },
    { top: '18%', right: '8%' },
    { top: '42%', left: '3%' },
    { top: '55%', right: '5%' },
    { top: '70%', left: '8%' },
    { top: '78%', right: '10%' },
    { top: '30%', left: '35%' },
    { top: '85%', left: '40%' },
  ];

  const cards = [
    { id: 'biography', label: t.hero.cards.biography, icon: User },
    { id: 'writing', label: t.hero.cards.writing, icon: FileText },
    { id: 'resume', label: t.hero.cards.resume, icon: Download },
    { id: 'contact', label: t.hero.cards.contact, icon: Mail },
  ];

  return (
    <section
      id="hero"
      ref={heroRef}
      className="relative min-h-screen flex items-center overflow-hidden paper-texture"
      style={{ paddingTop: '110px' }}
    >
      {/* Courthouse background, very low opacity */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          backgroundImage: `url(${COURTHOUSE_BG})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          opacity: 0.09,
          filter: 'grayscale(50%) contrast(1.1) brightness(0.7)',
        }}
      />
      {/* Vignette + tone */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            'radial-gradient(1200px 800px at 50% 40%, rgba(196,163,90,0.06), transparent 60%), linear-gradient(180deg, #0A0E1A 0%, rgba(10,14,26,0.9) 45%, #0A0E1A 100%)',
        }}
      />
      {/* Cursor spotlight */}
      <div className="absolute inset-0 spotlight" />

      {/* Floating citation fragments */}
      {CITATIONS.slice(0, 8).map((c, i) => {
        const pos = citationPositions[i];
        const parallaxX = (mouse.x - 0.5) * (i % 2 === 0 ? 20 : -16);
        const parallaxY = (mouse.y - 0.5) * (i % 2 === 0 ? -12 : 14);
        return (
          <motion.span
            key={c}
            className="citation-frag hidden md:block"
            style={{ ...pos, transform: `translate(${parallaxX}px, ${parallaxY}px)` }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1.8, delay: 0.3 + i * 0.1 }}
          >
            {c}
          </motion.span>
        );
      })}

      <div className="container-custom relative z-10 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center py-16">
        {/* LEFT: Portrait */}
        <motion.div
          initial={{ opacity: 0, x: -30 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 1, delay: 0.3, ease: 'easeOut' }}
          className="lg:col-span-3 flex justify-center lg:justify-start"
        >
          <div className="portrait-frame w-[270px] md:w-[300px] lg:w-full max-w-[320px]">
            <div className="chiaroscuro relative overflow-hidden" style={{ aspectRatio: '3/4' }}>
              <img
                src={PROFILE.portraitUrl}
                alt="Kenneth Christopher KC Redding at the Alabama State Capitol"
                className="w-full h-full object-cover"
                style={{ objectPosition: 'center 35%' }}
                data-testid="hero-portrait"
              />
              {/* Name overlay */}
              <div className="absolute bottom-0 left-0 right-0 p-4 z-10" style={{ background: 'linear-gradient(to top, rgba(10,14,26,0.95), transparent)' }}>
                <div className="font-heading text-[#C4A35A] text-sm tracking-[0.15em]">
                  K. C. REDDING
                </div>
                <div className="text-[9px] tracking-[0.3em] text-[#8a7a54] mt-1">
                  IMMIGRATION · FAMILY LAW
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* CENTER: Name + tagline */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.45, ease: 'easeOut' }}
          className="lg:col-span-6 text-center lg:text-left"
        >
          <div className="gold-rule mb-5 mx-auto lg:mx-0" />
          <div className="label-small mb-5">{t.intro.credential}</div>

          <h1 className="font-heading leading-[0.95] tracking-tight">
            <span className="block text-5xl md:text-6xl lg:text-7xl text-[#F0EDE8] font-light">Kenneth</span>
            <span className="block text-5xl md:text-6xl lg:text-7xl text-[#F0EDE8] font-light">Christopher</span>
            <span className="block text-6xl md:text-7xl lg:text-[5.5rem] italic font-medium mt-1" style={{ color: '#C4A35A' }}>
              &ldquo;KC&rdquo; Redding
            </span>
          </h1>

          <p className="font-heading italic text-xl md:text-2xl text-[#DCCFAD] mt-6">
            {PROFILE.tagline}
          </p>

          <div className="flex flex-wrap gap-2 mt-8 justify-center lg:justify-start">
            {PROFILE.specialties.map((s) => (
              <span key={s} className="tag-pill">{s}</span>
            ))}
          </div>

          <div className="flex gap-4 mt-6 text-sm text-[#DCCFAD] justify-center lg:justify-start">
            {PROFILE.languages.map((l) => (
              <span key={l.code} className="flex items-center gap-2">
                <span className="text-base">{l.flag}</span>
                <span className="tracking-[0.25em] uppercase text-[11px]">{l.code.toUpperCase()}</span>
              </span>
            ))}
          </div>
        </motion.div>

        {/* RIGHT: Quick-access cards + CTA */}
        <motion.div
          initial={{ opacity: 0, x: 30 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 1, delay: 0.6, ease: 'easeOut' }}
          className="lg:col-span-3"
        >
          <div className="label-small mb-4">{t.hero.quickAccess}</div>
          <div className="grid grid-cols-2 lg:grid-cols-1 gap-3">
            {cards.map((card) => {
              const Icon = card.icon;
              const isResume = card.id === 'resume';
              const handle = () => {
                if (isResume) return onQuickAccess?.('resume');
                if (card.id === 'contact') return navigate('/contact');
                scrollTo(card.id);
              };
              return (
                <button
                  key={card.id}
                  onClick={handle}
                  data-testid={`hero-card-${card.id}`}
                  className="group flex items-center justify-between border border-[rgba(196,163,90,0.25)] bg-[rgba(196,163,90,0.03)] px-4 py-3 hover:bg-[rgba(196,163,90,0.08)] hover:border-[#C4A35A] transition-all duration-300"
                >
                  <div className="flex items-center gap-3">
                    <Icon size={16} className="text-[#C4A35A]" />
                    <span className="text-[11px] tracking-[0.25em] uppercase text-[#DCCFAD] group-hover:text-[#F0EDE8]">
                      {card.label}
                    </span>
                  </div>
                  <ArrowUpRight size={14} className="text-[#8a7a54] group-hover:text-[#C4A35A] group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                </button>
              );
            })}
          </div>

          <button onClick={() => scrollTo('writing')} className="btn-gold w-full mt-6">
            <FileText size={14} />
            {t.hero.cta}
          </button>
        </motion.div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 scroll-indicator">
        <span className="text-[10px] tracking-[0.4em] text-[#8a7a54] uppercase">{t.hero.scroll}</span>
        <ChevronDown size={16} className="text-[#C4A35A]" />
      </div>
    </section>
  );
};

export default Hero;
